import {
  createSlice,
  createEntityAdapter,
  createSelector,
  PayloadAction,
} from "@reduxjs/toolkit";
import { Notification } from "main/types/notification";
import { isFulfilledAction, isRejectedAction } from "main/utils/sliceUtil";
import { RootState } from "main/store";

const key = "notification";
const adapter = createEntityAdapter<Notification.Notification>();

const initialStateValues: Notification.NotificationState = {
  notifications: [],
};

const initialState = adapter.getInitialState(initialStateValues);

export const uid = () => {
  return Date.now().toString(36) + Math.random().toString(36).substring(2);
};

const messageTexts = {
  "hotel/fetchAvailabilities/rejected": "",
  "preBooking/postPreBooking/rejected": "",
  "preBooking/postPreBookingPromotion/fulfilled":
    "notification.postPreBookingPromotion",
  "booking/postBooking/rejected": "",
  "preBooking/postBooking/rejected": "",
  "booking/cancelBooking/fulfilled": "notification.cancelBooking",
  "booking/cancelBooking/rejected": "",
  "booking/cancelBookingOption/fulfilled": "notification.cancelBookingOption",
  "booking/cancelBookingOption/rejected": "",
  "booking/cancelBookingRoom/fulfilled": "notification.cancelBookingRoom",
  "booking/cancelBookingRoom/rejected": "",
  "booking/putBooking/fulfilled": "notification.putBooking",
  "booking/putBooking/rejected": "",
  "booking/postBookingOptions/fulfilled": "notification.postBookingOptions",
  "booking/postBookingOptions/rejected": "",
  "booking/resendBookingMail/fulfilled": "notification.resendBookingMail",
  "booking/resendBookingMail/rejected": "",
  "user/putCurrentUser/fulfilled": "notification.putCurrentUser",
  "user/putCurrentUser/rejected": "",
  "user/deleteCurrentUser/fulfilled": "notification.deleteCurrentUser",
  "user/deleteCurrentUser/rejected": "",
  "user/requestUserEmailVerification/rejected": "",
};

const isNotificationActionType = (type) => {
  const keys = Object.keys(messageTexts);
  return keys.includes(type);
};

/** slice * */
export const notificationSlice = createSlice({
  name: key,
  initialState,
  reducers: {
    addNotification: (
      state,
      action: PayloadAction<Notification.Notification>,
    ) => {
      state.notifications = [action.payload].concat(state.notifications);
    },
    removeNotification: (state, action) => {
      state.notifications = state.notifications.filter(
        (notification) => notification.id !== action.payload,
      );
    },
    clearNotifications: (state) => {
      state.notifications = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(isRejectedAction(".+"), (state, action: any) => {
        if (isNotificationActionType(action.type)) {
          let errorMessages;
          if (action.payload?.messages) {
            errorMessages = action.payload?.messages;
          }
          if (errorMessages && errorMessages.length > 0) {
            const notification: Notification.Notification = {
              id: uid(),
              type: "error",
              message: errorMessages.join("\n"),
            };
            state.notifications = [notification].concat(state.notifications);
          }
        }
      })
      .addMatcher(isFulfilledAction(".+"), (state, action) => {
        if (isNotificationActionType(action.type)) {
          const notification: Notification.Notification = {
            id: uid(),
            type: "success",
            message: messageTexts[action.type],
          };
          state.notifications = [notification].concat(state.notifications);
        }
      });
  },
});

/** selector * */
const stateSelector = (state: RootState) => state[key];

export const notificationSelector = createSelector(
  stateSelector,
  (state) => state.notifications,
);

// action export
export const { addNotification, removeNotification, clearNotifications } =
  notificationSlice.actions;
